/*
 * socket_interface.js
 *
 * author: minchul lee (minchul.lee@aceworks.ai)
 * date: 2023.07.05
 */

import type {
  AuthCredentials,
  SignalingLoginResult,
  SignalingVehicleInfo,
} from "./auth_types";
import {
  APP_MESSAGE_TYPES,
  DATA_CHANNEL_CONFIG,
} from "../config/protocol_config";
import {
  CAMERA_TRACK_SLOTS,
  getConfiguredOfferMediaPlan,
  resolveCameraTrackSlot,
  resolveCameraTrackSlotByMediaSectionIndex,
} from "./webrtc_media_slots";
import type { CameraTrackSlot } from "./webrtc_media_slots";
import { applyPreferredH264VideoCodecPreferences } from "./webrtc_codec_preferences";

type EventCallbackMap = Record<string, Array<(...args: any[]) => void>>;

type PendingPromise<T> = {
  resolve: (value: T) => void;
  reject: (reason?: any) => void;
  roomId?: string;
} | null;

type TurnConfig = {
  username?: string;
  credential?: string;
} | null;

type PeerStateSource = "ice" | "connection" | "track" | "datachannel";

const DEFAULT_SIGNALING_WS_URL = "wss://plt.sdv.aceworks.ai:63001";
const DEFAULT_STUN_URL = "stun:stun.l.google.com:19302";
const DEFAULT_TURN_URL = "turn:plt.sdv.aceworks.ai:63478";

function toError(err: unknown, fallbackMessage: string): Error {
  if (err instanceof Error) {
    return err;
  }
  if (typeof err === "string") {
    return new Error(err);
  }
  return new Error(fallbackMessage);
}

function normalizeVehicleList(list: any): SignalingVehicleInfo[] {
  if (!Array.isArray(list)) {
    return [];
  }

  return list
    .map((item) => ({
      vehicleId: String(item?.vehicleId || ""),
      status: String(item?.status || "unavailable"),
    }))
    .filter((item) => Boolean(item.vehicleId)) as SignalingVehicleInfo[];
}

type ParsedSdpSections = {
  sessionLines: string[];
  mediaSections: string[][];
};

function parseSdpSections(sdp: string): ParsedSdpSections {
  const lines = sdp.split(/\r\n|\n/);
  const sessionLines: string[] = [];
  const mediaSections: string[][] = [];
  let currentMediaSection: string[] | null = null;

  lines.forEach((line) => {
    if (line.startsWith("m=")) {
      currentMediaSection = [line];
      mediaSections.push(currentMediaSection);
      return;
    }

    if (currentMediaSection) {
      currentMediaSection.push(line);
      return;
    }

    sessionLines.push(line);
  });

  return {
    sessionLines,
    mediaSections,
  };
}

function stringifySdpSections(parsed: ParsedSdpSections): string {
  const lines = [...parsed.sessionLines, ...parsed.mediaSections.flat()];
  const trimmedLines = [...lines];

  while (
    trimmedLines.length > 0 &&
    trimmedLines[trimmedLines.length - 1] === ""
  ) {
    trimmedLines.pop();
  }

  return `${trimmedLines.join("\r\n")}\r\n`;
}

function getMediaSectionKind(section: string[]): string {
  return section[0]?.slice(2).split(" ")[0] || "";
}

function getMediaSectionMid(section: string[]): string | null {
  const midLine = section.find((line) => line.startsWith("a=mid:"));
  return midLine ? midLine.slice("a=mid:".length) : null;
}

function replaceMediaSectionMid(section: string[], nextMid: string): void {
  const midIndex = section.findIndex((line) => line.startsWith("a=mid:"));
  if (midIndex < 0) {
    throw new Error("Missing a=mid line in media section");
  }
  section[midIndex] = `a=mid:${nextMid}`;
}

function rewriteBundleGroups(
  sessionLines: string[],
  replacements: Map<string, string>,
): string[] {
  return sessionLines.map((line) => {
    if (!line.startsWith("a=group:BUNDLE ")) {
      return line;
    }

    const tokens = line.slice("a=group:BUNDLE ".length).split(" ");
    const rewritten = tokens.map((token) => replacements.get(token) || token);
    return `a=group:BUNDLE ${rewritten.join(" ")}`;
  });
}

export function extractSdpMediaSections(
  sdp: string,
): Array<{ kind: string; mid: string | null }> {
  return parseSdpSections(sdp).mediaSections.map((section) => ({
    kind: getMediaSectionKind(section),
    mid: getMediaSectionMid(section),
  }));
}

export function applyConfiguredOfferMidsToSdp(
  sdp: string,
  config?: any,
): string {
  const parsed = parseSdpSections(sdp);
  const expectedPlan = getConfiguredOfferMediaPlan(config);

  if (parsed.mediaSections.length < expectedPlan.length) {
    throw new Error(
      `Expected at least ${expectedPlan.length} media sections, received ${parsed.mediaSections.length}`,
    );
  }

  const replacements = new Map<string, string>();

  expectedPlan.forEach((expected, index) => {
    const section = parsed.mediaSections[index];
    const actualKind = getMediaSectionKind(section);
    if (actualKind !== expected.kind) {
      throw new Error(
        `Unexpected media section kind at index ${index}: expected ${expected.kind}, received ${actualKind || "unknown"}`,
      );
    }

    const currentMid = getMediaSectionMid(section);
    if (!currentMid) {
      throw new Error(`Missing media mid at index ${index}`);
    }

    replacements.set(currentMid, expected.mid);
    replaceMediaSectionMid(section, expected.mid);
  });

  parsed.sessionLines = rewriteBundleGroups(parsed.sessionLines, replacements);
  return stringifySdpSections(parsed);
}

export function validateConfiguredOfferMediaMids(
  sdp: string,
  config?: any,
): void {
  const actualSections = extractSdpMediaSections(sdp);
  const expectedPlan = getConfiguredOfferMediaPlan(config);

  if (actualSections.length < expectedPlan.length) {
    throw new Error(
      `Expected at least ${expectedPlan.length} media sections, received ${actualSections.length}`,
    );
  }

  expectedPlan.forEach((expected, index) => {
    const actual = actualSections[index];
    if (actual.kind !== expected.kind) {
      throw new Error(
        `Unexpected remote media section kind at index ${index}: expected ${expected.kind}, received ${actual.kind || "unknown"}`,
      );
    }
    if (actual.mid !== expected.mid) {
      throw new Error(
        `Unexpected remote media mid at index ${index}: expected ${expected.mid}, received ${actual.mid || "null"}`,
      );
    }
  });
}

function resolveTrackEventCameraSlot(
  pc: RTCPeerConnection,
  evt: RTCTrackEvent,
  config?: any,
): CameraTrackSlot | null {
  const mid = evt.transceiver?.mid || null;
  const slotFromMid = resolveCameraTrackSlot(mid, config);
  if (slotFromMid) {
    return slotFromMid;
  }

  if (!evt.transceiver || typeof pc.getTransceivers !== "function") {
    return null;
  }

  const transceiverIndex = pc.getTransceivers().indexOf(evt.transceiver);
  return resolveCameraTrackSlotByMediaSectionIndex(transceiverIndex, config);
}

export default class SocketInterface {
  config: any;
  connection_url: string;
  peerConnectionMap: Record<string, RTCPeerConnection>;
  pendingIceCandidateMap: Record<string, RTCIceCandidateInit[]>;
  dataChannelMap: Record<string, RTCDataChannel>;
  dataChannel: RTCDataChannel | null;
  private ccFeedbackTimer: ReturnType<typeof setInterval> | null = null;
  private ccPrevLost = 0;
  private ccPrevRecv = 0;
  private ccPrevBytes = 0;
  private ccPrevTime = 0;
  ws: WebSocket | undefined;
  binded_f: EventCallbackMap;
  private wsOpenPromise: Promise<WebSocket> | null;
  private loginPending: PendingPromise<SignalingLoginResult>;
  private listPending: PendingPromise<SignalingVehicleInfo[]>;
  private joinPending: PendingPromise<void>;
  private currentCredentials: AuthCredentials | null;
  private lastLoginResult: SignalingLoginResult | null;
  private cachedVehicleList: SignalingVehicleInfo[];
  private activeRoomId: string | null;
  private activeTurnConfig: TurnConfig;
  // WebRTC 연결 실패 자동 복구 — 첫 시도에 ICE/connection 이 failed 되면 stale pc 가
  // shouldStartOffer 가드를 막아 재연결이 안 되던 문제(요청)를 해결하기 위한 재시도 상태.
  private connectionRetryCount: Record<string, number> = {};
  private connectionRetryTimers: Record<string, ReturnType<typeof setTimeout>> =
    {};
  // Connect watchdog: proactively re-offer if a connection never reaches "connected"
  // within CONNECT_ESTABLISH_TIMEOUT_MS. ICE can stall in "checking"/"disconnected"
  // on a flaky first attempt WITHOUT ever reaching "failed", so the failed-only recovery
  // never fires — this is the main cause of the unreliable first connect.
  private connectionEstablishTimers: Record<
    string,
    ReturnType<typeof setTimeout>
  > = {};
  // "disconnected" 는 대부분 일시적 ICE blip 으로 수 초 내 스스로 "connected" 로
  // 회복된다. 이때 즉시 재offer 하면 회복 중인 연결을 강제 teardown 해서 차량이
  // peerconnection=failed 를 찍고 영상이 끊겼다 재개되는 플래핑이 생긴다. 그래서
  // "disconnected" 는 유예 타이머를 두고, 회복 못 할 때만 복구한다.
  private connectionDisconnectGraceTimers: Record<
    string,
    ReturnType<typeof setTimeout>
  > = {};
  // 연결이 "일정 시간 계속 유지"됐을 때만 재시도 카운트를 리셋하기 위한 타이머.
  // 즉시 리셋하면 플래핑 시 매 짧은 재연결마다 카운트가 0이 되어 MAX_RETRIES/백오프가
  // 영원히 발동하지 못한다(무한 플래핑). 진짜 안정과 순간 재연결을 구분한다.
  private connectionStableTimers: Record<
    string,
    ReturnType<typeof setTimeout>
  > = {};

  constructor(config: any) {
    this.config = config;
    this.connection_url = this.resolveSignalingUrl();
    this.peerConnectionMap = {};
    this.pendingIceCandidateMap = {};
    this.dataChannelMap = {};
    this.dataChannel = null;
    this.ws = undefined;
    this.binded_f = {};
    this.wsOpenPromise = null;
    this.loginPending = null;
    this.listPending = null;
    this.joinPending = null;
    this.currentCredentials = null;
    this.lastLoginResult = null;
    this.cachedVehicleList = [];
    this.activeRoomId = null;
    this.activeTurnConfig = null;
  }

  registerEvent(key: string, f: (...args: any[]) => void, pointer: any): void {
    const handler = pointer ? f.bind(pointer) : f.bind(null);
    if (!this.binded_f[key]) {
      this.binded_f[key] = [];
    }
    this.binded_f[key].push(handler);
  }

  async login(credentials: AuthCredentials): Promise<SignalingLoginResult> {
    if (!credentials?.userId || !credentials?.password) {
      return Promise.reject(new Error("userId and password are required"));
    }

    const sameUser =
      this.currentCredentials?.userId === credentials.userId &&
      this.currentCredentials?.password === credentials.password;

    if (sameUser && this.lastLoginResult && this.isWebSocketOpen()) {
      return this.lastLoginResult;
    }

    if (!sameUser && this.ws) {
      this.disconnectSession();
    }

    this.currentCredentials = credentials;
    const ws = await this.ensureWebSocket();

    return new Promise((resolve, reject) => {
      this.loginPending?.reject(new Error("Signaling login superseded"));
      this.loginPending = { resolve, reject };
      this.sendSignaling(ws, {
        type: "login",
        payload: {
          username: credentials.userId,
          password: credentials.password,
        },
      });
    });
  }

  async listVehicles(): Promise<SignalingVehicleInfo[]> {
    if (!this.isWebSocketOpen() || !this.lastLoginResult) {
      return Promise.reject(new Error("Signaling login is not ready"));
    }

    const ws = this.ws!;
    return new Promise((resolve, reject) => {
      this.listPending?.reject(new Error("Vehicle list request superseded"));
      this.listPending = { resolve, reject };
      this.sendSignaling(ws, { type: "list" });
    });
  }

  getCachedVehicleList(): SignalingVehicleInfo[] {
    return [...this.cachedVehicleList];
  }

  async joinVehicle(vehicleId: string): Promise<void> {
    if (!vehicleId) {
      return Promise.reject(new Error("vehicleId is required"));
    }
    if (!this.isWebSocketOpen() || !this.lastLoginResult) {
      return Promise.reject(new Error("Signaling login is not ready"));
    }

    if (this.activeRoomId && this.activeRoomId !== vehicleId) {
      this.cleanupPeer(this.activeRoomId);
    }

    this.activeRoomId = vehicleId;

    return new Promise((resolve, reject) => {
      this.joinPending?.reject(new Error("Join request superseded"));
      this.joinPending = { resolve, reject, roomId: vehicleId };
      this.sendSignaling(this.ws, {
        type: "join",
        payload: {
          roomId: vehicleId,
        },
      });
    });
  }

  disconnectSession(): void {
    this.rejectPending(new Error("Signaling session disconnected"));

    Object.keys(this.dataChannelMap).forEach((roomId) => {
      const dc = this.dataChannelMap[roomId];
      if (dc && dc.readyState !== "closed") {
        dc.close();
      }
    });
    Object.keys(this.peerConnectionMap).forEach((roomId) => {
      this.cleanupPeer(roomId);
    });
    this.pendingIceCandidateMap = {};

    if (this.ws) {
      try {
        this.ws.close();
      } catch (err) {
        console.error(
          "[SocketInterface] Failed to close signaling socket:",
          err,
        );
      }
    }

    this.ws = undefined;
    this.wsOpenPromise = null;
    this.lastLoginResult = null;
    this.cachedVehicleList = [];
    this.activeRoomId = null;
    this.activeTurnConfig = null;
    this.dataChannel = null;
  }

  disconnectVehicle(): void {
    if (!this.activeRoomId) {
      return;
    }
    this.cleanupPeer(this.activeRoomId);
    this.activeRoomId = null;
    this.activeTurnConfig = null;
    this.dataChannel = null;
  }

  send(message: string): void {
    if (this.dataChannel != null && this.dataChannel.readyState === "open") {
      this.dataChannel.send(message);
    }
  }

  isDataChannelOpen(): boolean {
    return Boolean(
      this.dataChannel != null && this.dataChannel.readyState === "open",
    );
  }

  private resolveSignalingUrl(): string {
    return (
      this.config?.signalingWsUrl ||
      this.config?.signaling_ws_url ||
      DEFAULT_SIGNALING_WS_URL
    );
  }

  // private resolveStunUrl(): string {
  //   return (
  //     this.config?.signalingStunUrl ||
  //     this.config?.signaling_stun_url ||
  //     DEFAULT_STUN_URL
  //   );
  // }

  private resolveTurnUrl(): string {
    return (
      this.config?.signalingTurnUrl ||
      this.config?.signaling_turn_url ||
      DEFAULT_TURN_URL
    );
  }

  private buildRtcConfiguration(turnConfig: TurnConfig): RTCConfiguration {
    const iceServers: RTCIceServer[] = [
      // {
      //   urls: this.resolveStunUrl(),
      // },
    ];

    if (turnConfig?.username && turnConfig?.credential) {
      iceServers.push({
        urls: this.resolveTurnUrl(),
        username: turnConfig.username,
        credential: turnConfig.credential,
      });
    }

    return { iceServers, iceTransportPolicy: "relay" };
  }

  private isWebSocketOpen(): boolean {
    return Boolean(this.ws && this.ws.readyState === WebSocket.OPEN);
  }

  private ensureWebSocket(): Promise<WebSocket> {
    if (this.isWebSocketOpen() && this.ws) {
      return Promise.resolve(this.ws);
    }

    if (this.wsOpenPromise) {
      return this.wsOpenPromise;
    }

    this.wsOpenPromise = new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(this.connection_url);
      this.ws = ws;

      ws.onopen = () => {
        this.wsOpenPromise = null;
        resolve(ws);
      };

      ws.onerror = () => {
        this.invokeEvent("error", new Error("WebSocket error"));
        this.wsOpenPromise = null;
        reject(new Error("WebSocket error"));
      };

      ws.onclose = () => {
        console.error("WebSocket disconnected");
        const error = new Error("WebSocket disconnected");
        this.invokeEvent("error", error);
        this.rejectPending(error);
        this.wsOpenPromise = null;
        this.ws = undefined;
      };

      ws.onmessage = (e) => {
        if (typeof e.data !== "string") {
          return;
        }

        try {
          const message = JSON.parse(e.data);
          void this.handleSignalingMessage(message);
        } catch (err) {
          console.error(
            "[SocketInterface] Failed to parse signaling message:",
            err,
          );
        }
      };
    }).catch((err) => {
      this.ws = undefined;
      throw err;
    });

    return this.wsOpenPromise;
  }

  private async handleSignalingMessage(message: any): Promise<void> {
    const type = String(message?.type || "");
    const payload = message?.payload;

    switch (type) {
      case "login-success": {
        const normalized = this.normalizeLoginResult(payload);
        this.lastLoginResult = normalized;
        if (normalized.vehicleList) {
          this.cachedVehicleList = normalized.vehicleList;
        }
        this.loginPending?.resolve(normalized);
        this.loginPending = null;
        return;
      }

      case "list-response": {
        const vehicleList = normalizeVehicleList(payload?.vehicleList);
        this.cachedVehicleList = vehicleList;
        this.listPending?.resolve(vehicleList);
        this.listPending = null;
        return;
      }

      case "join-success": {
        const roomId = this.joinPending?.roomId || this.activeRoomId;
        if (!roomId) {
          return;
        }

        this.activeRoomId = roomId;
        this.activeTurnConfig = payload?.turnConfig || null;
        // 새 세션 진입 — 자동 복구 재시도 예산 리셋(이전 세션의 소진 카운트가 남지 않게).
        this.connectionRetryCount[roomId] = 0;
        this.clearConnectionRetryTimer(roomId);

        try {
          const shouldStartOffer =
            this.shouldInitiateOffer() &&
            payload?.isPeerPresent !== false &&
            this.shouldStartOffer(roomId);
          if (shouldStartOffer) {
            console.log("[SocketInterface] Starting local offer", {
              roomId,
              source: "join-success",
            });
            await this.startLocalOffer(roomId, this.activeTurnConfig);
          }
          this.joinPending?.resolve();
        } catch (err) {
          console.error(
            "[SocketInterface] Failed to establish local offer:",
            err,
          );
          const error = toError(
            err,
            "Failed to establish local offer after join",
          );
          this.joinPending?.reject(error);
          this.invokeEvent("error", error);
        } finally {
          this.joinPending = null;
        }
        return;
      }

      case "offer":
      case "answer":
        if (type === "offer" && this.shouldInitiateOffer()) {
          console.warn(
            "[SocketInterface] Ignoring remote offer because cockpit initiates offer",
          );
          return;
        }
        await this.handleRemoteDescription(
          type as "offer" | "answer",
          payload || {},
        );
        return;

      case "ice-candidate":
        await this.handleRemoteCandidate(payload || {});
        return;

      case "peer-joined": {
        if (
          this.activeRoomId &&
          this.shouldInitiateOffer() &&
          this.shouldStartOffer(this.activeRoomId)
        ) {
          try {
            console.log("[SocketInterface] Starting local offer", {
              roomId: this.activeRoomId,
              source: "peer-joined",
            });
            await this.startLocalOffer(
              this.activeRoomId,
              this.activeTurnConfig,
            );
          } catch (err) {
            console.error(
              "[SocketInterface] Failed to establish local offer:",
              err,
            );
            this.invokeEvent(
              "error",
              toError(err, "Failed to establish local offer"),
            );
          }
        }
        this.invokeEvent(type, payload);
        return;
      }

      case "vehicle-joined":
      case "cockpit-joined":
        this.invokeEvent(type, payload);
        return;

      case "login-failed":
      case "list-failed":
      case "join-failed":
      case "error": {
        const error = new Error(
          payload?.message ||
            payload?.error ||
            `${type} received from signaling server`,
        );
        this.rejectMatchingPending(type, error);
        this.invokeEvent("error", error);
        return;
      }

      default:
        return;
    }
  }

  private shouldInitiateOffer(): boolean {
    return this.lastLoginResult?.role !== "vehicle";
  }

  private shouldStartOffer(roomId: string): boolean {
    const pc = this.peerConnectionMap[roomId];
    if (!pc || pc.signalingState === "closed") {
      return true;
    }

    if (
      pc.signalingState === "have-local-offer" ||
      pc.signalingState === "have-remote-offer"
    ) {
      return false;
    }

    const dc = this.dataChannelMap[roomId];
    if (dc && dc.readyState !== "closed") {
      return false;
    }

    return !pc.localDescription && !pc.remoteDescription;
  }

  private normalizeLoginResult(payload: any): SignalingLoginResult {
    if (typeof payload === "string") {
      return {
        role: payload === "vehicle" ? "vehicle" : "cockpit",
      };
    }

    return {
      role: payload?.role === "vehicle" ? "vehicle" : "cockpit",
      vehicleList: normalizeVehicleList(payload?.vehicleList),
    };
  }

  private rejectMatchingPending(type: string, error: Error): void {
    if (type === "login-failed") {
      this.loginPending?.reject(error);
      this.loginPending = null;
      return;
    }

    if (type === "list-failed") {
      this.listPending?.reject(error);
      this.listPending = null;
      return;
    }

    if (type === "join-failed") {
      this.joinPending?.reject(error);
      this.joinPending = null;
      return;
    }

    this.rejectPending(error);
  }

  private async handleRemoteDescription(
    type: "offer" | "answer",
    payload: RTCSessionDescriptionInit,
  ): Promise<void> {
    const roomId = this.activeRoomId || this.joinPending?.roomId || "default";
    let pc = this.peerConnectionMap[roomId];

    if (!pc && type === "answer") {
      return;
    }

    if (!pc) {
      pc = this.createPeerConnection(roomId, this.activeTurnConfig);
    }

    if (type === "answer" && pc.signalingState !== "have-local-offer") {
      return;
    }

    if (type === "offer" && payload.sdp) {
      validateConfiguredOfferMediaMids(payload.sdp, this.config);
    }

    await pc.setRemoteDescription(payload);
    await this.flushPendingIceCandidates(roomId, pc);
    if (type === "offer") {
      await this.invokeEventAsync("offerConnection", pc);
      await this.sendLocalDescription(roomId, pc, "answer");
    }
  }

  private async handleRemoteCandidate(
    payload: RTCIceCandidateInit,
  ): Promise<void> {
    const roomId = this.activeRoomId || this.joinPending?.roomId || "default";
    const pc = this.peerConnectionMap[roomId];
    if (!payload?.candidate) {
      return;
    }

    if (!pc || !pc.remoteDescription) {
      if (!this.pendingIceCandidateMap[roomId]) {
        this.pendingIceCandidateMap[roomId] = [];
      }
      this.pendingIceCandidateMap[roomId].push(payload);
      return;
    }

    try {
      await pc.addIceCandidate(payload);
    } catch (err) {
      console.warn("[SocketInterface] Ignoring remote ICE candidate", {
        roomId,
        reason: err instanceof Error ? err.message : String(err),
        sdpMid: payload.sdpMid ?? null,
        sdpMLineIndex: payload.sdpMLineIndex ?? null,
        signalingState: pc.signalingState,
        remoteDescriptionType: pc.remoteDescription?.type ?? null,
      });
    }
  }

  private async startLocalOffer(
    roomId: string,
    turnConfig: TurnConfig,
  ): Promise<void> {
    this.cleanupPeer(roomId);
    this.activeRoomId = roomId;

    const pc = this.createPeerConnection(roomId, turnConfig);
    const videoTransceivers = CAMERA_TRACK_SLOTS.map(() =>
      pc.addTransceiver("video", {
        direction: "recvonly",
      }),
    );
    pc.addTransceiver("audio", {
      direction: "recvonly",
    });
    pc.addTransceiver("audio", {
      direction: "sendonly",
    });
    applyPreferredH264VideoCodecPreferences(videoTransceivers);
    const dc = pc.createDataChannel(DATA_CHANNEL_CONFIG.label);
    this.setupDataChannel(dc, roomId);

    await this.invokeEventAsync("offerConnection", pc);
    await this.sendLocalDescription(roomId, pc, "offer");
  }

  private createPeerConnection(
    roomId: string,
    turnConfig: TurnConfig,
  ): RTCPeerConnection {
    const pc = new RTCPeerConnection(this.buildRtcConfiguration(turnConfig));
    pc.oniceconnectionstatechange = () => {
      this.emitPeerState(roomId, "ice");
      this.handlePeerHealth(roomId, pc);
    };
    if ("onconnectionstatechange" in pc) {
      pc.onconnectionstatechange = () => {
        this.emitPeerState(roomId, "connection");
        this.handlePeerHealth(roomId, pc);
      };
    }
    this.armConnectWatchdog(roomId, pc);
    pc.onicecandidate = (e) => {
      if (e.candidate?.candidate) {
        console.log("[SocketInterface] Local ICE candidate", {
          roomId,
          sdpMid: e.candidate.sdpMid ?? null,
          sdpMLineIndex: e.candidate.sdpMLineIndex ?? null,
        });
        this.sendLocalCandidate(roomId, e.candidate);
      }
    };
    pc.ondatachannel = (e) => {
      this.emitPeerState(roomId, "datachannel", {
        label: e.channel.label,
        dataChannelState: e.channel.readyState,
      });
      this.setupDataChannel(e.channel, roomId);
    };
    pc.ontrack = (evt) => {
      const cameraSlot = resolveTrackEventCameraSlot(pc, evt, this.config);
      this.emitPeerState(roomId, "track", {
        mid: evt.transceiver?.mid ?? null,
        trackKind: evt.track?.kind ?? null,
        cameraSlot,
      });
      this.invokeEvent("onTrack", {
        ...evt,
        receiver: evt.receiver,
        streams: evt.streams,
        track: evt.track,
        transceiver: evt.transceiver,
        cameraSlot,
      });
    };

    this.peerConnectionMap[roomId] = pc;
    return pc;
  }

  private setupDataChannel(dc: RTCDataChannel, roomId: string): RTCDataChannel {
    this.dataChannel = dc;
    dc.onopen = () => {
      this.emitPeerState(roomId, "datachannel", {
        label: dc.label,
        dataChannelState: dc.readyState,
      });
      this.startCcFeedback(dc, roomId);
    };
    dc.onclose = () => {
      this.stopCcFeedback();
      this.emitPeerState(roomId, "datachannel", {
        label: dc.label,
        dataChannelState: dc.readyState,
      });
    };
    dc.onmessage = (e) => {
      if (typeof e.data === "string") {
        try {
          const msg = JSON.parse(e.data);
          if (
            msg?.__MESSAGE__ === APP_MESSAGE_TYPES.connection &&
            msg.status === false
          ) {
            this.invokeEvent("error");
          }
        } catch (_err) {
          // Non-JSON messages are forwarded as-is.
        }
      }
      this.invokeEvent("message", e);
    };

    this.dataChannelMap[roomId] = dc;
    return dc;
  }

  /**
   * Congestion-control feedback: the cockpit measures real packet loss via
   * getStats() and reports it to the vehicle over the data channel (~1 Hz).
   * libdatachannel on the vehicle can't surface RTCP RR to its send tracks, so
   * this is how the vehicle's dynamic CC learns the actual network condition.
   */
  private startCcFeedback(dc: RTCDataChannel, roomId: string): void {
    this.stopCcFeedback();
    this.ccPrevLost = 0;
    this.ccPrevRecv = 0;
    this.ccPrevBytes = 0;
    this.ccPrevTime = 0;
    this.ccFeedbackTimer = setInterval(() => {
      const pc = this.peerConnectionMap[roomId];
      if (!pc || dc.readyState !== "open") return;
      pc.getStats()
        .then((stats) => {
          let lost = 0;
          let recv = 0;
          let bytes = 0;
          stats.forEach((r: any) => {
            if (r.type === "inbound-rtp" && r.kind === "video") {
              lost += r.packetsLost || 0;
              recv += r.packetsReceived || 0;
              bytes += r.bytesReceived || 0;
            }
          });
          const dLost = Math.max(0, lost - this.ccPrevLost);
          const dRecv = Math.max(0, recv - this.ccPrevRecv);
          this.ccPrevLost = lost;
          this.ccPrevRecv = recv;
          const denom = dLost + dRecv;
          const loss = denom > 0 ? dLost / denom : 0;
          // received throughput (kbps) = the real capacity the link is delivering
          const now = performance.now();
          let recvKbps = 0;
          if (this.ccPrevTime > 0) {
            const dt = (now - this.ccPrevTime) / 1000;
            if (dt > 0) recvKbps = ((bytes - this.ccPrevBytes) * 8) / 1000 / dt;
          }
          this.ccPrevBytes = bytes;
          this.ccPrevTime = now;
          if (dc.readyState === "open") {
            dc.send(
              JSON.stringify({
                __MESSAGE__: "cc_feedback",
                loss: +loss.toFixed(4),
                recvKbps: Math.round(recvKbps),
              }),
            );
          }
        })
        .catch(() => {});
      // 400ms (was 1000ms): faster loss feedback so the vehicle CC reacts to a sudden
      // network drop in ~1s instead of ~6s, cutting bitrate before the video breaks.
    }, 400);
  }

  private stopCcFeedback(): void {
    if (this.ccFeedbackTimer != null) {
      clearInterval(this.ccFeedbackTimer);
      this.ccFeedbackTimer = null;
    }
  }

  private emitPeerState(
    roomId: string,
    source: PeerStateSource,
    extra: Record<string, any> = {},
  ): void {
    const pc = this.peerConnectionMap[roomId];
    this.invokeEvent("peerState", {
      roomId,
      source,
      iceConnectionState: pc?.iceConnectionState ?? null,
      connectionState: pc?.connectionState ?? null,
      signalingState: pc?.signalingState ?? null,
      ...extra,
    });
  }

  private async sendLocalDescription(
    _roomId: string,
    pc: RTCPeerConnection,
    type: "offer" | "answer",
  ): Promise<void> {
    if (type === "offer" && pc.signalingState !== "stable") {
      if (pc.signalingState === "have-remote-offer") {
        console.warn(
          "[SocketInterface] Skipping local offer because a remote offer already won",
        );
        return;
      }
      throw new Error(
        `Cannot create ${type} in signalingState=${pc.signalingState}`,
      );
    }

    const desc =
      type === "offer" ? await pc.createOffer() : await pc.createAnswer();
    if (type === "offer" && pc.signalingState !== "stable") {
      if (pc.signalingState === "have-remote-offer") {
        console.warn(
          "[SocketInterface] Skipping local offer because a remote offer already won",
        );
        return;
      }
      throw new Error(
        `Cannot apply ${type} in signalingState=${pc.signalingState}`,
      );
    }

    const localDesc =
      type === "offer"
        ? {
            type: desc.type,
            sdp: applyConfiguredOfferMidsToSdp(desc.sdp || "", this.config),
          }
        : desc;

    try {
      await pc.setLocalDescription(localDesc);
    } catch (err) {
      if (type === "offer" && pc.signalingState === "have-remote-offer") {
        console.warn(
          "[SocketInterface] Skipping local offer because a remote offer already won",
        );
        return;
      }
      throw err;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error("Signaling socket is not ready");
    }

    this.sendSignaling(this.ws, {
      type,
      payload: {
        type: pc.localDescription?.type,
        sdp: pc.localDescription?.sdp,
      },
    });
    console.log("[SocketInterface] Local description sent", {
      roomId: _roomId,
      type,
      signalingState: pc.signalingState,
      sdpLength: pc.localDescription?.sdp?.length ?? 0,
    });
  }

  private sendLocalCandidate(_roomId: string, cand: RTCIceCandidate): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this.sendSignaling(this.ws, {
      type: "ice-candidate",
      payload: {
        candidate: cand.candidate,
        sdpMid: cand.sdpMid,
        sdpMLineIndex: cand.sdpMLineIndex,
      },
    });
  }

  private sendSignaling(ws: WebSocket | undefined, message: any): void {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }

    ws.send(JSON.stringify(message));
  }

  private cleanupPeer(roomId: string): void {
    const dc = this.dataChannelMap[roomId];
    if (dc && dc.readyState !== "closed") {
      dc.close();
    }
    delete this.dataChannelMap[roomId];

    const pc = this.peerConnectionMap[roomId];
    if (pc) {
      pc.close();
    }
    delete this.peerConnectionMap[roomId];
    delete this.pendingIceCandidateMap[roomId];

    this.clearConnectionRetryTimer(roomId);
    this.clearConnectionEstablishTimer(roomId);
    this.clearConnectionDisconnectGraceTimer(roomId);
    this.clearConnectionStableTimer(roomId);

    if (this.activeRoomId === roomId) {
      this.dataChannel = null;
      this.activeRoomId = null;
    }
  }

  private clearConnectionRetryTimer(roomId: string): void {
    const timer = this.connectionRetryTimers[roomId];
    if (timer) {
      clearTimeout(timer);
      delete this.connectionRetryTimers[roomId];
    }
  }

  private clearConnectionEstablishTimer(roomId: string): void {
    const timer = this.connectionEstablishTimers[roomId];
    if (timer) {
      clearTimeout(timer);
      delete this.connectionEstablishTimers[roomId];
    }
  }

  private clearConnectionDisconnectGraceTimer(roomId: string): void {
    const timer = this.connectionDisconnectGraceTimers[roomId];
    if (timer) {
      clearTimeout(timer);
      delete this.connectionDisconnectGraceTimers[roomId];
    }
  }

  private clearConnectionStableTimer(roomId: string): void {
    const timer = this.connectionStableTimers[roomId];
    if (timer) {
      clearTimeout(timer);
      delete this.connectionStableTimers[roomId];
    }
  }

  // Arm a one-shot watchdog for a freshly created pc: if it hasn't established within
  // CONNECT_ESTABLISH_TIMEOUT_MS, schedule a recovery re-offer. Catches the stuck-ICE
  // first-attempt case that never reaches "failed" (so handlePeerHealth wouldn't fire).
  private armConnectWatchdog(roomId: string, pc: RTCPeerConnection): void {
    const CONNECT_ESTABLISH_TIMEOUT_MS = 8000;
    this.clearConnectionEstablishTimer(roomId);
    this.connectionEstablishTimers[roomId] = setTimeout(() => {
      delete this.connectionEstablishTimers[roomId];
      if (this.peerConnectionMap[roomId] !== pc) {
        return; // stale pc (replaced/cleaned up)
      }
      const cs = String(pc.connectionState || "");
      const is = String(pc.iceConnectionState || "");
      const established =
        cs === "connected" || is === "connected" || is === "completed";
      if (!established) {
        console.warn(
          "[SocketInterface] connect watchdog fired — not established, re-offering",
          { roomId, connectionState: cs, iceConnectionState: is },
        );
        this.scheduleConnectionRecovery(roomId);
      }
    }, CONNECT_ESTABLISH_TIMEOUT_MS);
  }

  // ICE/connection 상태 변화 시 자동 복구 판단. 정상 연결되면 재시도 카운트를 리셋하고,
  // failed 면(첫 시도 실패=사용자가 본 "안되는 경우") 재offer 로 자동 복구를 예약한다.
  private handlePeerHealth(roomId: string, pc: RTCPeerConnection): void {
    if (this.peerConnectionMap[roomId] !== pc) {
      return; // 이미 교체/정리된 오래된 pc 의 이벤트 — 무시.
    }
    const connectionState = String(pc.connectionState || "");
    const iceState = String(pc.iceConnectionState || "");

    if (
      connectionState === "connected" ||
      iceState === "connected" ||
      iceState === "completed"
    ) {
      this.clearConnectionRetryTimer(roomId);
      this.clearConnectionEstablishTimer(roomId);
      this.clearConnectionDisconnectGraceTimer(roomId); // 회복됨 — 유예 취소
      // 재시도 카운트는 즉시 0으로 리셋하지 않는다. 계속 STABLE_CONFIRM_MS 동안 연결이
      // 유지될 때만 리셋해, 짧은 재연결→즉시 리셋→다시 드롭의 무한 플래핑을 막는다.
      if (!this.connectionStableTimers[roomId]) {
        const STABLE_CONFIRM_MS = 12000;
        this.connectionStableTimers[roomId] = setTimeout(() => {
          delete this.connectionStableTimers[roomId];
          if (this.peerConnectionMap[roomId] !== pc) {
            return;
          }
          const stable =
            pc.connectionState === "connected" ||
            pc.iceConnectionState === "connected" ||
            pc.iceConnectionState === "completed";
          if (stable) {
            this.connectionRetryCount[roomId] = 0;
          }
        }, STABLE_CONFIRM_MS);
      }
      return;
    }

    // "failed" 는 확정 실패 → 즉시 복구.
    if (connectionState === "failed" || iceState === "failed") {
      this.clearConnectionDisconnectGraceTimer(roomId);
      this.clearConnectionStableTimer(roomId); // 안정 확정 취소(아직 재시도 카운트 유지)
      this.scheduleConnectionRecovery(roomId);
      return;
    }

    // "disconnected" 는 대부분 일시적 blip. 즉시 재offer 하면 회복 중인 연결을 강제
    // teardown 해서 차량이 failed 를 찍고 영상 끊김/재개 플래핑이 생긴다. 유예시간을
    // 두고, 그 안에 "connected" 로 회복되면 위 분기에서 타이머가 취소되어 아무것도
    // 안 하고, 회복 못 하면 그때 복구한다.
    if (iceState === "disconnected") {
      this.clearConnectionStableTimer(roomId); // 더 이상 안정 아님 — 카운트 리셋 취소
      if (this.connectionDisconnectGraceTimers[roomId]) {
        return; // 이미 유예 중.
      }
      // relay-only 환경에서 "disconnected"는 대부분 릴레이 리턴 경로의 실제 break(자가회복
      // 안 됨)라, 긴 유예는 드롭 시간만 늘린다. 짧게(2.5s) 두어 실제 break 는 빨리 복구하되
      // 순간적 blip 은 여전히 걸러낸다.
      const DISCONNECT_GRACE_MS = 2500;
      this.connectionDisconnectGraceTimers[roomId] = setTimeout(() => {
        delete this.connectionDisconnectGraceTimers[roomId];
        if (this.peerConnectionMap[roomId] !== pc) {
          return; // 교체/정리된 pc.
        }
        const cs = String(pc.connectionState || "");
        const is = String(pc.iceConnectionState || "");
        const healed =
          cs === "connected" || is === "connected" || is === "completed";
        if (!healed) {
          console.warn(
            "[SocketInterface] ICE disconnected did not self-heal — recovering",
            { roomId, connectionState: cs, iceConnectionState: is },
          );
          this.scheduleConnectionRecovery(roomId);
        }
      }, DISCONNECT_GRACE_MS);
    }
  }

  // 실패한 pc 를 정리하고 새 offer 로 재협상을 시도한다(콕핏이 offer 주체일 때만).
  // stale pc 가 남아 shouldStartOffer 가드가 재offer 를 막던 문제를 해소한다.
  // 무한 루프 방지를 위해 최대 횟수/백오프를 둔다. 정상 연결 시 카운트는 리셋된다.
  private scheduleConnectionRecovery(roomId: string): void {
    if (!this.shouldInitiateOffer()) {
      return; // 차량이 offer 주체면 차량이 재협상.
    }
    if (this.activeRoomId !== roomId) {
      return; // 더 이상 활성 세션이 아니면(나가기 등) 복구하지 않는다.
    }
    if (this.connectionRetryTimers[roomId]) {
      return; // 이미 복구 예약됨.
    }

    const MAX_RETRIES = 4;
    const attempt = (this.connectionRetryCount[roomId] || 0) + 1;
    if (attempt > MAX_RETRIES) {
      console.error("[SocketInterface] WebRTC recovery exhausted", {
        roomId,
        attempt,
      });
      this.invokeEvent(
        "error",
        new Error("WebRTC connection failed after retries"),
      );
      return;
    }
    this.connectionRetryCount[roomId] = attempt;

    const delayMs = Math.min(4000, 600 * attempt);
    this.connectionRetryTimers[roomId] = setTimeout(() => {
      delete this.connectionRetryTimers[roomId];
      if (this.activeRoomId !== roomId) {
        return;
      }
      console.warn("[SocketInterface] Recovering WebRTC connection (re-offer)", {
        roomId,
        attempt,
      });
      // 복구는 full re-offer(cleanupPeer→pc.close→새 pc)로 한다. pc.close() 가 TURN
      // dealloc(Refresh lifetime=0)을 보내 coturn 의 allocation 이 정리되므로, ICE restart
      // 방식처럼 old allocation 이 dealloc 없이 버려져 coturn 에 stale 세션이 누적되고
      // watchdog 이 그걸 reap 하면서 "active 세션의 relay→콕핏 forwarding 까지 죽이는"
      // ~30초 주기 플래핑을 막는다. (라이브 getStats 로 리턴 경로 동시 사망 확인.)
      // startLocalOffer→createPeerConnection 경로라 connect watchdog 도 재무장된다.
      void this.startLocalOffer(roomId, this.activeTurnConfig).catch((err) => {
        console.error("[SocketInterface] WebRTC recovery re-offer failed:", err);
      });
    }, delayMs);
  }

  private async flushPendingIceCandidates(
    roomId: string,
    pc: RTCPeerConnection,
  ): Promise<void> {
    const pendingCandidates = this.pendingIceCandidateMap[roomId];
    if (!pendingCandidates || pendingCandidates.length === 0) {
      return;
    }

    delete this.pendingIceCandidateMap[roomId];

    for (const candidate of pendingCandidates) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (err) {
        console.warn("[SocketInterface] Ignoring queued remote ICE candidate", {
          roomId,
          reason: err instanceof Error ? err.message : String(err),
          sdpMid: candidate.sdpMid ?? null,
          sdpMLineIndex: candidate.sdpMLineIndex ?? null,
          signalingState: pc.signalingState,
          remoteDescriptionType: pc.remoteDescription?.type ?? null,
        });
      }
    }
  }

  private rejectPending(error: Error): void {
    this.loginPending?.reject(error);
    this.listPending?.reject(error);
    this.joinPending?.reject(error);
    this.loginPending = null;
    this.listPending = null;
    this.joinPending = null;
  }

  private invokeEvent(key: string, ...args: any[]): void {
    const handlers = this.binded_f[key];
    if (!handlers || handlers.length === 0) {
      return;
    }
    handlers.forEach((handler) => {
      try {
        handler(...args);
      } catch (err) {
        console.error(
          `[SocketInterface] Event handler failed for ${key}:`,
          err,
        );
      }
    });
  }

  private async invokeEventAsync(key: string, ...args: any[]): Promise<void> {
    const handlers = this.binded_f[key];
    if (!handlers || handlers.length === 0) {
      return;
    }

    for (const handler of handlers) {
      try {
        await Promise.resolve(handler(...args));
      } catch (err) {
        console.error(
          `[SocketInterface] Event handler failed for ${key}:`,
          err,
        );
      }
    }
  }
}
